/*
 * #1013 Stockist finder - postcode search over the Stockist metaobjects.
 * Distances are straight-line km from the postcode's centre point to each store pin.
 * Postcode centre points: GeoNames postal code data (CC BY 4.0), assets/stockist-postcodes-my.json.
 */
(() => {
  if (customElements.get('stockist-finder')) return;

  let postcodesPromise;
  const loadPostcodes = (url) => {
    postcodesPromise =
      postcodesPromise ||
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.json();
        })
        .catch((e) => {
          postcodesPromise = null;
          throw e;
        });
    return postcodesPromise;
  };

  const escapeHtml = (v) =>
    String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const km = (lat1, lng1, lat2, lng2) => {
    const r = (x) => (x * Math.PI) / 180;
    const h =
      Math.sin(r(lat2 - lat1) / 2) ** 2 + Math.cos(r(lat1)) * Math.cos(r(lat2)) * Math.sin(r(lng2 - lng1) / 2) ** 2;
    return 6371 * 2 * Math.asin(Math.sqrt(h));
  };

  // Exact postcode first; an unlisted one falls back to the average of its 3-digit, then 2-digit, neighbours.
  const locate = (postcodes, pc) => {
    if (postcodes[pc]) return postcodes[pc];
    for (const len of [3, 2]) {
      const near = Object.keys(postcodes).filter((k) => k.slice(0, len) === pc.slice(0, len));
      if (near.length) {
        return [
          near.reduce((s, k) => s + postcodes[k][0], 0) / near.length,
          near.reduce((s, k) => s + postcodes[k][1], 0) / near.length,
        ];
      }
    }
    return null;
  };

  class StockistFinder extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-stockist-input]');
      this.output = this.querySelector('[data-stockist-results]');
      this.stores = JSON.parse(this.querySelector('[data-stockist-data]').textContent)
        .map((s) => ({ ...s, lat: parseFloat(s.lat), lng: parseFloat(s.lng) }))
        .filter((s) => !isNaN(s.lat) && !isNaN(s.lng));

      this.querySelector('[data-stockist-submit]').addEventListener('click', () => this.search());
      // The black-sauce PDP renders this inside the add-to-cart form: Enter must search, never submit the form.
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.search();
      });
    }

    async search() {
      const pc = this.input.value.replace(/\s+/g, '');
      if (!/^\d{5}$/.test(pc)) return this.render(`<p class="stockist-finder__error">${escapeHtml(this.dataset.msgInvalid)}</p>`);

      this.setAttribute('aria-busy', 'true');
      let origin;
      try {
        origin = locate(await loadPostcodes(this.dataset.postcodesUrl), pc);
      } catch (e) {
        this.removeAttribute('aria-busy');
        return this.render(`<p class="stockist-finder__error">${escapeHtml(this.dataset.msgError)}</p>`);
      }
      this.removeAttribute('aria-busy');
      if (!origin) return this.render(`<p class="stockist-finder__error">${escapeHtml(this.dataset.msgUnknown)}</p>`);

      const maxKm = Number(this.dataset.maxKm);
      const ranked = this.stores
        .map((s) => ({ s, d: km(origin[0], origin[1], s.lat, s.lng) }))
        .sort((a, b) => a.d - b.d);
      const near = ranked.filter((r) => r.d <= maxKm).slice(0, Number(this.dataset.limit));

      if (near.length) {
        const head = this.dataset.msgFound
          .replace('[count]', near.length)
          .replace('[stores]', near.length === 1 ? this.dataset.wordStore : this.dataset.wordStores)
          .replace('[km]', maxKm)
          .replace('[postcode]', pc);
        return this.render(`<p class="stockist-finder__result-head">${escapeHtml(head)}</p>${near.map((r) => this.card(r.s, r.d)).join('')}`);
      }

      const nearest = ranked[0];
      const head = this.dataset.msgNone.replace('[km]', maxKm).replace('[postcode]', pc);
      const nearestLine = nearest
        ? `<p class="stockist-finder__nearest">${escapeHtml(
            this.dataset.msgNearest.replace('[store]', nearest.s.n).replace('[distance]', Math.round(nearest.d))
          )}</p>`
        : '';
      const cta = this.dataset.ctaUrl
        ? `<a class="stockist-finder__cta" href="${escapeHtml(this.dataset.ctaUrl)}">${escapeHtml(this.dataset.ctaLabel)}</a>`
        : '';
      this.render(
        `<div class="stockist-finder__none"><p class="stockist-finder__none-title">${escapeHtml(head)}</p>${nearestLine}<p>${escapeHtml(
          this.dataset.msgOnline
        )}</p>${cta}</div>`
      );
    }

    card(s, d) {
      const directions = `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
      return `<div class="stockist-card">
        <p class="stockist-card__name"><span>${escapeHtml(s.n)}</span><span class="stockist-card__km">${d.toFixed(1)} km</span></p>
        ${s.a ? `<p class="stockist-card__line">${escapeHtml(s.a)}</p>` : ''}
        ${s.h ? `<p class="stockist-card__line">${escapeHtml(s.h)}</p>` : ''}
        ${s.p ? `<p class="stockist-card__line">${escapeHtml(s.p)}</p>` : ''}
        <a class="stockist-card__link" href="${directions}" target="_blank" rel="noopener">${escapeHtml(this.dataset.wordDirections)}</a>
      </div>`;
    }

    render(html) {
      this.output.innerHTML = html;
    }
  }

  customElements.define('stockist-finder', StockistFinder);
})();
